/**
 * Script tool processor for the Now Assist Skill Plugin.
 * Handles processing of Script tools defined in NowAssistSkillConfig.
 */

import {
    TOOL_TYPES,
    RESOURCE_ATTRIBUTE_VALUE_SCRIPT,
    SYS_ONE_EXTEND_CAPABILITY_DEFINITION_API_TYPES,
    SCRIPT_TOOL_API_ID,
} from '../defaults'
import type { Context, ObjectShape, Record as BuildRecord } from '@servicenow/sdk-build-core'
import { applyDefaults, buildDependsOnList, createDependencyEdges } from '../skill-plugin-helpers'
import type { ToolDefinition } from '../tool-types'
import {
    SYS_ONE_EXTEND_RESOURCE_MAPPING,
    SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING,
    SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE,
    getApplicabilityProperties,
} from '../tool-utils'
import {
    createToolCapabilityInfra,
    createInputDefinitionAttributes,
    createInputAttributeMappings,
} from './tool-capability-helpers'

/**
 * Process a Script tool and create the necessary records
 *
 * Script tools create:
 * 1. sys_one_extend_resource_mapping - The main tool record
 * 2. sys_one_extend_resource_attribute_mapping - Input attribute mappings
 * 3. sys_one_extend_resource_attribute_mapping - Output attribute mapping (fixed: Output, type: object)
 * 4. sys_one_extend_resource_edge - Dependency edges (if depends is specified)
 */
export async function processScriptTool(
    tool: ToolDefinition,
    capabilityRecord: BuildRecord,
    toolRecordsMap: Map<string, BuildRecord>,
    arg: ObjectShape,
    factory: Context['factory'],
    diagnostics: Context['diagnostics'],
    decisionToolNames?: Set<string>
): Promise<BuildRecord[]> {
    const records: BuildRecord[] = []

    // Validate tool type
    if (tool.type !== TOOL_TYPES.SCRIPT) {
        diagnostics.error(arg, `Expected script tool but got ${tool.type}`)
        return records
    }

    // Validate required fields
    if (!tool.name) {
        diagnostics.error(arg, 'Script tool requires a name')
        return records
    }

    // Build depends_on_list from dependency names
    const dependsOnList = buildDependsOnList(tool, toolRecordsMap, arg, diagnostics, decisionToolNames)

    // Build metadata with scriptId, scriptFunctionName, and toolType for reverse detection
    const metadata: Record<string, unknown> = {
        toolType: TOOL_TYPES.SCRIPT,
    }
    if (tool.scriptId) {
        metadata['scriptId'] = tool.scriptId
    }
    if (tool.scriptFunctionName) {
        metadata['scriptFunctionName'] = tool.scriptFunctionName
    }

    // Build capability definition metadata (scriptId + scriptFunctionName)
    const capDefMetadata: Record<string, unknown> = {}
    if (tool.scriptFunctionName) {
        capDefMetadata['scriptFunctionName'] = tool.scriptFunctionName
    }
    if (tool.scriptId) {
        capDefMetadata['scriptId'] = tool.scriptId
    }

    // Build capability name: {scriptId}_{toolName}
    const capabilityName = tool.scriptId ? `${tool.scriptId}_${tool.name}` : tool.name

    // Build capability definition name: {scriptId}_{toolName}.{scriptFunctionName}
    const capDefName = tool.scriptFunctionName ? `${capabilityName}.${tool.scriptFunctionName}` : capabilityName

    // 0a-0c. Create capability infrastructure records
    const infraResult = await createToolCapabilityInfra(
        tool,
        capabilityRecord,
        capabilityName,
        {
            name: capDefName,
            api: SCRIPT_TOOL_API_ID,
            apiType: SYS_ONE_EXTEND_CAPABILITY_DEFINITION_API_TYPES.SYS_HUB_ACTION_TYPE_DEFINITION,
            metadata: JSON.stringify(capDefMetadata),
        },
        arg,
        factory
    )
    records.push(...infraResult.records)
    const { toolCapabilityRecord, toolCapabilityExplicitId } = infraResult

    // 0d. Create sys_one_extend_definition_attribute records for inputs
    const { inputDefAttrRecordsMap, records: inputDefAttrRecords } = await createInputDefinitionAttributes(
        tool,
        toolCapabilityRecord,
        arg,
        factory
    )
    records.push(...inputDefAttrRecords)

    // 0e. Create sys_one_extend_definition_attribute record for output
    // If output is provided, use its $id; otherwise derive from the tool's capability explicitId
    const outputExplicitId =
        tool.output?.$id ?? (toolCapabilityExplicitId ? `${toolCapabilityExplicitId}__output` : undefined)
    const outputName = tool.output?.name ?? 'output'
    const outputLabel = outputName.charAt(0).toUpperCase() + outputName.slice(1)
    const outputDefAttrRecord = await factory.createRecord({
        source: tool._source!,
        table: SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE,
        ...(outputExplicitId ? { explicitId: outputExplicitId } : {}),
        properties: arg.transform(({ $ }) => ({
            ...applyDefaults(SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE, $),
            capability: $.val(toolCapabilityRecord.getId()),
            name: $.val(outputName),
            label: $.val(outputLabel),
            type: $.val('output'),
            mandatory: $.val(true),
        })),
    })
    records.push(outputDefAttrRecord)

    // 1. Create sys_one_extend_resource_mapping record
    const resourceMappingRecord = await factory.createRecord({
        source: tool._source!,
        table: SYS_ONE_EXTEND_RESOURCE_MAPPING,
        ...(tool.$id ? { explicitId: tool.$id } : {}),
        properties: arg.transform(({ $ }) => ({
            ...applyDefaults(SYS_ONE_EXTEND_RESOURCE_MAPPING, $),
            ...getApplicabilityProperties(tool.condition, $),
            depends_on_list: $.val(dependsOnList),
            metadata: $.val(JSON.stringify(metadata)),
            parent_capability: $.val(capabilityRecord.getId()),
            resource_capability: $.val(toolCapabilityRecord.getId()),
            resource_name: $.val(tool.name),
        })),
    })
    records.push(resourceMappingRecord)

    // Store in map for dependency resolution
    toolRecordsMap.set(tool.name, resourceMappingRecord)

    // 2. Create sys_one_extend_resource_attribute_mapping for inputs
    const inputAttrRecords = await createInputAttributeMappings(
        tool,
        resourceMappingRecord,
        arg,
        factory,
        inputDefAttrRecordsMap
    )
    records.push(...inputAttrRecords)

    // 3. Create sys_one_extend_resource_attribute_mapping for output
    // Script has fixed output: { name: 'Output', type: 'object' }
    const truncate = tool.truncate ?? false
    const outputAttributeRecord = await factory.createRecord({
        source: tool._source!,
        table: SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING,
        properties: arg.transform(({ $ }) => ({
            ...applyDefaults(SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING, $),
            attribute_value_script: $.val(RESOURCE_ATTRIBUTE_VALUE_SCRIPT).toCdata(),
            resource_attribute_name: $.val(outputDefAttrRecord.getId()),
            resource_mapping: $.val(resourceMappingRecord.getId()),
            truncate: $.val(truncate),
            type: $.val('output'),
        })),
    })
    records.push(outputAttributeRecord)

    // 4. Create sys_one_extend_resource_edge records for dependencies
    const edgeRecords = await createDependencyEdges(
        tool,
        toolRecordsMap,
        resourceMappingRecord,
        arg,
        factory,
        true,
        tool._source!
    )
    records.push(...edgeRecords)

    return records
}
